/* Card geometry, read out of IDCARD_95x130mm_with_QRCODE.ai.
   PDF points, origin bottom-left, on a 95 x 130 mm artboard.
   These are measurements, not preferences — if the artwork changes, re-read it. */

export const MM = 72 / 25.4;
export const CARD_W = 269.291;   // 95 mm
export const CARD_H = 368.504;   // 130 mm

export const PANEL_TOP = 187.256;
export const PANEL_Y = 0.379;
export const PANEL_H = PANEL_TOP - PANEL_Y;

/* The QR as drawn on the artwork was 94.6 pt square. On the 109 mm stock that
   came out at 28 mm, and with a payload of a couple of hundred characters its
   modules were down to about 0.38 mm -- readable, but with little to spare for
   a worn badge or a cheap print. It is drawn 15% larger.

   Growing it in place would push 14 pt into the text beside and above it. So
   part of the growth is taken from the margins instead: the square moves 5 pt
   toward the right-hand trim and 4 pt toward the bottom. Its own two-module
   quiet zone is inside the square, so the dark modules still sit about 3 mm
   in from the cut on the 109 mm card -- clear of any trimming error. */
const QR_ARTWORK = { x: 161.871, y: 15.752, w: 95.999, h: 94.574 };
const QR_SIDE = Math.min(QR_ARTWORK.w, QR_ARTWORK.h) * 1.15;
const QR_RIGHT = QR_ARTWORK.x + (QR_ARTWORK.w + Math.min(QR_ARTWORK.w, QR_ARTWORK.h)) / 2 + 5;
export const QR_BOX = { x: QR_RIGHT - QR_SIDE, y: QR_ARTWORK.y - 4, w: QR_SIDE, h: QR_SIDE };

const RIGHT_FULL = CARD_W - 14.2539;        // text that clears the QR block
const RIGHT_BESIDE_QR = QR_BOX.x - 6;       // text that sits beside it

export const NAME = {
  x: 14.2539, baseline: 151.084, size: 23.8461, weight: 'bold',
  maxWidth: RIGHT_FULL - 14.2539, minSize: 13, maxLines: 2, wrap: 'up',
  leading: 1.08,
};
/* Everything under the name flows as one block. Each entry is a small label
   above its value, so a reader can tell at a glance whether a line is the
   company, the pillar or the job title — on the artwork alone they are three
   similar-looking lines. The label is smaller and lighter than the value it
   describes, and never appears on its own: a guest with no pillar gets neither
   the pillar nor its heading. */
export const DETAILS = {
  x: 14.4922,
  firstBaseline: 123.1357,
  bottom: 45,                // clears the category's cap height with a gap

  size: 13.6298,             // the value
  minSize: 9,
  leading: 1.06,

  labelSize: 7.2,            // the heading above it
  labelMinSize: 6,
  labelGrey: 0.42,           // recedes without becoming unreadable in print

  /* A heading must sit nearer the value it names than the one above it, or the
     eye pairs them the wrong way round. 11.5 is about as tight as the label's
     descender and the value's cap height allow. */
  valueDrop: 11.5,           // label baseline -> its value baseline
  pairGap: 14,               // value baseline -> next label baseline

  qrTop: QR_BOX.y + QR_BOX.h,
  rightFull: RIGHT_FULL,
  rightBesideQr: RIGHT_BESIDE_QR,
};

/* What can go on a badge under the name, in the order it is drawn, with the
   heading printed above each. */
export const DETAIL_FIELDS = [
  { key: 'company',    label: 'Company',                 card: 'COMPANY / ORGANIZATION' },
  { key: 'pillar',     label: 'Pillar',                  card: 'SINAR MAS BUSINESS PILLAR' },
  { key: 'position',   label: 'Position / jabatan',      card: 'POSITION' },
  { key: 'parentName', label: 'Host, for family badges', card: 'GUEST OF' },
];
export const DEFAULT_FIELDS = ['company', 'pillar'];

export const CATEGORIES = [
  { slug: 'vvip',        label: 'VVIP',        size: 34.671,  baseline: 19.1743, x: 16.8682, colour: '#9E1F63', ink: '#8A1B57', aliases: [] },
  { slug: 'vip',         label: 'VIP',         size: 34.671,  baseline: 19.1743, x: 16.8682, colour: '#652D90', ink: '#5A2880', aliases: [] },
  { slug: 'vip-guest',   label: 'VIP guest',   size: 24.5114, baseline: 22.1001, x: 16.8682, colour: '#EB008B', ink: '#C1006F', aliases: ['vip guest', 'guest vip', 'vipguest'] },
  { slug: 'all-access',  label: 'all access',  size: 27.6172, baseline: 23.2056, x: 16.8672, colour: '#EC1C24', ink: '#C4141B', aliases: ['all access', 'allaccess'] },
  { slug: 'committee',   label: 'committee',   size: 25.0712, baseline: 21.939,  x: 16.8691, colour: '#8BC53F', ink: '#5C8A1C', aliases: ['panitia'] },
  { slug: 'participant', label: 'participant', size: 24.5114, baseline: 22.1001, x: 16.8691, colour: '#2B388F', ink: '#2B388F', aliases: ['peserta', 'attendee'] },
  { slug: 'family',      label: 'family',      size: 24.5114, baseline: 22.1001, x: 16.8691, colour: '#DE33E2', ink: '#A927AC', aliases: ['keluarga', 'guest family'] },
];

export const BY_SLUG = Object.fromEntries(CATEGORIES.map(c => [c.slug, c]));
export const PRINT_ORDER = CATEGORIES.map(c => c.slug);

export function categorySlot(cat) {
  return {
    x: cat.x, baseline: cat.baseline, size: cat.size, weight: 'bold',
    maxWidth: RIGHT_BESIDE_QR - cat.x, minSize: 12, maxLines: 1, wrap: 'up',
    leading: 1.08,
  };
}

const LOOKUP = {};
for (const c of CATEGORIES) {
  LOOKUP[c.slug] = c.slug;
  LOOKUP[c.label.toLowerCase()] = c.slug;
  LOOKUP[c.slug.replace('-', ' ')] = c.slug;
  for (const a of c.aliases) LOOKUP[a] = c.slug;
}

export function normaliseCategory(raw) {
  if (!raw) return null;
  const key = String(raw).trim().toLowerCase().replace(/\s+/g, ' ');
  return LOOKUP[key] || LOOKUP[key.replace(/ /g, '-')] ||
         LOOKUP[key.replace(/-/g, ' ')] || null;
}
